import React, { FC, Suspense, useEffect, useState } from 'react';
import {
  ExpiredRodRequestIconStyled,
  MenuButtonStyled,
  MenuItemStyled,
  PopperContainerStyled,
  GrabButtonStyled,
} from './styled';
import {
  Box,
  Icon,
  IconSize,
  TextFieldSize,
  MenuOptions,
  Menu,
  useSideSheetState,
  Button,
  ButtonAppearance,
  ButtonSize,
  Spinner,
  SpinnerSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { Trans, t } from '@lingui/macro';
import { useMutation, ApolloError } from '@apollo/client';
import { ASSIGN_UNASSIGN_PRIORITY_COMMODITY, GET_COMMODITY_PRIORITY_COUNT } from '@bechtel/client-gql';
import {
  CommodityStatusCode,
  PersonaType,
  AssignUnassignPriorityInput,
  AssignUnassignPriorityError,
  WorkPackageFilterByTerm,
  CommodityAssignmentError,
  AssignCommoditiesInput,
  AssignCommodityInput,
  CommodityAssignee,
  CommodityList,
  CommodityFilterInput,
  CommodityFilterByTerm,
  Project,
  FilterOperator,
  BlockStatusCode,
  Block,
  CommodityType,
  ProjectMilestoneType,
} from '@bechtel/types';
import { WeldAction } from './action';
import { CreateBlock } from '@bechtel/client-blocks';
import { css } from 'styled-components';
import {
  ErrorBoundary,
  SuspenseLoader,
  useBlockPermission,
  useModulePermission,
  useConfig,
  useWeldAssignees,
  useAppUser,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { WeldDetailActionsProps } from './types';
import { WeldDetailsSidesheet } from '../../weld-details-sidesheet';
import { useNavigate } from 'react-router-dom';
import {
  useWeldPermissions,
  useRodRoomPermissions,
  useAssignCommodityCrewMembers,
  useProjectCommodityTypesById,
} from '~/hooks';

import { AssignUnassignPriorityErrorModal } from './error';
import { AssignWeldOwner } from '../../assign-weld-owner';
import { drawerValues as defaultValues } from './form-state';
import { WeldSendBackSideSheet } from '../../weld-send-back-sidesheet';
import { get, indexOf, map, find, toLower, some, flatMap } from 'lodash';
import { AssignCommodityCrewMembersErrorModal } from '~/components/molecules/assign-error-modal';
import { EditWeldDetails } from '~/components';
import { useLazyQuery } from '@apollo/client';
import { useProjectMilestoneType } from '~/hooks/use-project-milestone-type';
export const WeldDetailActions: FC<WeldDetailActionsProps> = ({
  weld,
  personaType,
  createRodRequest,
  isVisibleOnWeldSubHeader,
}) => {
  const refetchQueries = [
    'commodities',
    'commodityStats',
    'commodityStatsPriority',
    'commodityFitupStats',
    'commodityStatsClaimedAndUnassignedFWE',
    'commodityStatsRejectedFWE',
    'commodityStatsAllPriorityStats',
    'commodityBlocksStatsFWE',
    'assignToMeStatsFWE',
    'commodityDashboardTabStats',
  ];
  const { user } = useAppUser();
  const dispatchToast = useToastsDispatch();
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [unAssignCommodityCrewMembersErrors, setUnassignCommodityCrewMembersErrors] = useState<
    CommodityAssignmentError[] | ApolloError | null
  >();
  const blocks = flatMap(weld.milestones, 'blocks');
  const hasOpenBlocks = some(blocks, (item: Block) => item.currentStatusI18n == BlockStatusCode.BLOCK_OPENED);
  const weldStatus = hasOpenBlocks ? CommodityStatusCode.BLOCKED : weld.status?.status;
  const weldCurrentMilestone = weld?.milestones.find((milestone) => {
    return milestone?.type?.id === weld?.currentMilestoneType?.id;
  });

  const fitUpMilestone = weld?.milestones.find((milestone) => {
    return milestone?.name?.toUpperCase() === WeldMilestoneType.FITUP;
  });

  const weldExpiredRodRequestCount = weld?.expiredRodRequestCount || 0;
  const { id = ' ', tag = ' ', facilities = [] } = weld && weld?.workPackage ? weld.workPackage : {};
  const { commodityAssignees } = weld;
  const workPackageOwners = [
    ...(weld.workPackage?.primaryOwners ?? []),
    ...(weld.workPackage?.secondaryOwners ?? []),
    ...(weld.workPackage?.otherOwners ?? []),
  ];

  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const {
    canRejectFitup,
    canAssignWeldPriority,
    canReassignWeldForeman,
    canAssignFieldWeldEngineer,
  } = useWeldPermissions(assignedCrewMembers);

  const { canCreateRodRequest } = useRodRoomPermissions(assignedCrewMembers);
  const assignedCrewMemberIds = map(assignedCrewMembers, (assignedCrewMember) => {
    return assignedCrewMember?.assignee?.id;
  });
  const isFitupMilestone = weld?.currentMilestoneType?.name?.toUpperCase() == WeldMilestoneType.FITUP;
  const isWeldingRequired = weld?.fitupRequest?.isWeldingRequired ? weld?.fitupRequest?.isWeldingRequired : false;
  const isFitUpRequired = weld?.fitupRequest?.isFitupRequired ? weld?.fitupRequest?.isFitupRequired : false;
  const canReAssignForeman =
    (isFitupMilestone ? isWeldingRequired : !isFitupMilestone) &&
    canReassignWeldForeman &&
    assignedCrewMemberIds.length > 0;

  const { canCreateBlock } = useBlockPermission(
    workPackageOwners,
    facilities ? facilities : [],
    null,
    assignedCrewMembers
  );
  const { features } = useConfig();
  const {
    visible: visibleCreateBlock,
    open: openCreateBlockSideSheet,
    close: closeCreateBlockSideSheet,
  } = useSideSheetState(false);
  const { visible: visibleWeldSidesheet, open: openWeldSidesheet, close: closeWeldSidesheet } = useSideSheetState(
    false
  );
  const {
    visible: visibleEditWeldDetailsSideSheet,
    open: openEditWeldDetailsSideSheet,
    close: closeEditWeldDetailsSideSheet,
  } = useSideSheetState(false);
  const {
    visible: visibleSendBackPipingSidesheet,
    open: openSendBackPipingSidesheet,
    close: closeSendBackPipingSidesheet,
  } = useSideSheetState(false);
  const [assignUnassignPriorityError, setAssignUnassignPriorityError] = useState<AssignUnassignPriorityError | null>();

  const { visible, open, close } = useSideSheetState(false);
  const navigate = useNavigate();
  const [updatePriority, { loading: mutationLoading }] = useMutation<{ input: AssignUnassignPriorityInput }>(
    ASSIGN_UNASSIGN_PRIORITY_COMMODITY,
    {
      onError: (error) => {
        setAssignUnassignPriorityError(error);
      },
      refetchQueries: [...refetchQueries, 'commodityPriorityCount'],
    }
  );
  const closeModal = () => {
    setAssignUnassignPriorityError(null);
  };
  const closedUnassignModal = () => {
    setUnassignCommodityCrewMembersErrors(null);
  };

  const [getProject, { called: projectCalled, data: projectData }] = useProjectCommodityTypesById();
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;

  const [loadCommodityPriorityCount, { data }] = useLazyQuery<
    { commodities: CommodityList },
    {
      input: CommodityFilterInput;
    }
  >(GET_COMMODITY_PRIORITY_COUNT, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const markedPriorityWelds = get(data, 'commodities.priorityCommodityCount', 0);
  const showPriorityLabel = () => {
    if (markedPriorityWelds < features?.priorityWelds?.total && !weld.isPriority) {
      return t`Mark as Priority Weld`;
    }

    if (weld.isPriority) {
      return t`Remove as Priority`;
    }
  };
  const [
    assignUnassignCommodityCrewMembers,
    {
      error: assignCommodityCrewMemberError,
      data: assignCommodityCrewMemberData,
      data: assignCommodityCrewMemberLoading,
    },
  ] = useAssignCommodityCrewMembers(() => {
    setActionCompleted(true);
  }, refetchQueries);

  useEffect(() => {
    if (!actionCompleted) return;
    const erroredCommodityAssignments =
      get(assignCommodityCrewMemberData, 'assignCommodityCrewMembers.erroredCommodityAssignments') ||
      assignCommodityCrewMemberError;
    if (erroredCommodityAssignments && erroredCommodityAssignments?.length == 0) {
      setUnassignCommodityCrewMembersErrors(null);
      const unassignCommodityCrewMemberToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${t`Weld assignee updated successfully.`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(unassignCommodityCrewMemberToast);
      close && close();
    } else {
      setUnassignCommodityCrewMembersErrors(erroredCommodityAssignments);
    }
    setActionCompleted(false);
  }, [actionCompleted]);

  useEffect(() => {
    if (!user || !user.project || projectCalled) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
  }, [user]);

  useEffect(() => {
    if (!weldTypeId) {
      return;
    }

    loadCommodityPriorityCount({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
                value: weldTypeId,
                operator: FilterOperator.EQUALS,
              },
            ],
          },
        },
      },
    });
  }, [weldTypeId]);
  const { canViewWorkPackageModule, canViewRodRoomModule } = useModulePermission();
  const isMenuButtonVisible =
    canViewWorkPackageModule ||
    weldStatus === CommodityStatusCode.READY_TO_WELD ||
    weldStatus === CommodityStatusCode.FITUP_INSPECTION ||
    !isVisibleOnWeldSubHeader;

  const showPriorityWeldOption =
    canAssignWeldPriority &&
    features?.priorityWelds?.enabled &&
    weldStatus === CommodityStatusCode.READY_TO_WELD &&
    (markedPriorityWelds < features?.priorityWelds?.total || weld.isPriority);

  const [
    getProjectMilestoneList,
    { error: projectMilestoneError, data: projectMilestoneList },
  ] = useProjectMilestoneType(user?.project?.id);

  useEffect(() => {
    if (!user || !user.project || !user.project.id) return;

    getProjectMilestoneList({
      variables: {
        id: user.project.id,
      },
    });
  }, [user]);

  const projectCommodityType = find(
    projectMilestoneList?.projectConfigById.projectCommodityTypes,
    (item: CommodityType) => item.lobCommodityTypeId == 'Weld'
  );

  const fitupProjectMilestoneDetails = find(
    projectCommodityType?.projectMilestoneTypes,
    (item: ProjectMilestoneType) => item.lobMilestoneTypeId === WeldMilestoneType.FITUP
  );

  return (
    <>
      <Box justifyContent={{ sm: 'flex-end' }} data-testid="weld-status-actions">
        {isVisibleOnWeldSubHeader &&
          personaType === PersonaType.FIELD_WELD_ENGINEER &&
          (weldStatus === CommodityStatusCode.CLAIMED || weldStatus === CommodityStatusCode.VT_SENT_BACK) &&
          !hasOpenBlocks &&
          indexOf(assignedCrewMemberIds, user.id) == -1 &&
          canAssignFieldWeldEngineer && (
            <GrabButtonStyled
              onClick={() => {
                const assignCommodityInput: AssignCommodityInput = {
                  commodityId: weld?.id,
                  crewMemberId: user?.id,
                  personaType: personaType,
                };
                const input: AssignCommoditiesInput = {
                  input: [assignCommodityInput],
                };
                assignUnassignCommodityCrewMembers({
                  variables: { input },
                });
              }}
              appearance={ButtonAppearance.SECONDARY}
              size={ButtonSize.SMALL}
            >
              {assignCommodityCrewMemberLoading && <Spinner />}
              {!assignCommodityCrewMemberLoading && t`Grab Weld`}
            </GrabButtonStyled>
          )}
        {canViewRodRoomModule &&
          !hasOpenBlocks &&
          canCreateRodRequest &&
          weldStatus &&
          [CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK].includes(weldStatus) && (
            <Box css={{ position: 'relative' }}>
              <Button
                data-testid="rod-request-button"
                onClick={() => createRodRequest()}
                appearance={ButtonAppearance.SECONDARY}
                css={`
                  ${({ theme }) => css`
                    margin-right: ${theme.spacings[300]};
                    min-width: 112px;
                    width: auto;
                    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%), 0 2px 1px -1px rgb(0 0 0 / 12%),
                      0 1px 1px 0 rgb(0 0 0 / 14%);
                  `}
                `}
                size={ButtonSize.SMALL}
              >
                <Icon icon="file-spreadsheet" fillColor="blue.800" />
                &nbsp; <Trans>Request Rod</Trans>
              </Button>
            </Box>
          )}
        <Box mr={{ sm: '300' }} flexShrink={{ sm: '0' }}>
          {weld && (
            <WeldAction
              status={weldStatus}
              milestone={weldCurrentMilestone}
              fitupProjectMilestoneDetails={fitupProjectMilestoneDetails}
              weldCommodity={weld}
              personaType={personaType}
            />
          )}
        </Box>
        {isMenuButtonVisible && (
          <Box css={{ position: 'relative' }} data-testid="list-item">
            <Menu>
              {({ isOpen, toggle, close: closeMenu }) => (
                <div
                  data-testid="close-menu"
                  onBlur={() => {
                    closeMenu();
                  }}
                >
                  <MenuButtonStyled
                    data-testid="weld-more-button"
                    onClick={() => {
                      toggle();
                    }}
                    size={TextFieldSize.SMALL}
                  >
                    <Icon icon="ellipsis-h" size={IconSize.SMALL} fillColor="blue.800" />
                    {weldExpiredRodRequestCount > 0 && <ExpiredRodRequestIconStyled>!</ExpiredRodRequestIconStyled>}
                  </MenuButtonStyled>
                  <PopperContainerStyled>
                    {isOpen && (
                      <MenuOptions
                        css={`
                          order: 2;
                          border-bottom-left-radius: 0px;
                          border-bottom-right-radius: 0px;
                        `}
                      >
                        <Box flexDirection={{ sm: 'row' }}>
                          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} pt={{ sm: '400' }}>
                            {!isVisibleOnWeldSubHeader && (
                              <MenuItemStyled
                                data-testid="weld-view-details"
                                onMouseDown={(event) => {
                                  openWeldSidesheet();
                                  event.preventDefault();
                                }}
                              >
                                <Trans>View Details</Trans>
                              </MenuItemStyled>
                            )}

                            {weldStatus !== CommodityStatusCode.VT_APPROVED &&
                              personaType == PersonaType.FIELD_WELD_ENGINEER && (
                                <MenuItemStyled
                                  onMouseDown={(event) => {
                                    openEditWeldDetailsSideSheet();
                                    event.preventDefault();
                                  }}
                                >
                                  <Trans>Edit Details</Trans>
                                </MenuItemStyled>
                              )}

                            {(weldStatus === CommodityStatusCode.READY_TO_WELD ||
                              weldStatus === CommodityStatusCode.VT_SENT_BACK ||
                              weldStatus === CommodityStatusCode.FITUP_INSPECTION) && (
                              <>
                                {canCreateBlock && (
                                  <MenuItemStyled
                                    data-testid="weld-create-block"
                                    onMouseDown={(event) => {
                                      openCreateBlockSideSheet();
                                      closeMenu(); //
                                      event.preventDefault();
                                    }}
                                  >
                                    <Trans>Create Block</Trans>
                                  </MenuItemStyled>
                                )}
                                {showPriorityWeldOption && (
                                  <MenuItemStyled
                                    data-testid="weld-priority"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      const input: AssignUnassignPriorityInput = {
                                        commodityId: weld.id,
                                        isPriority: !weld.isPriority,
                                      };
                                      updatePriority({
                                        variables: { input },
                                      });
                                    }}
                                  >
                                    {showPriorityLabel()}
                                  </MenuItemStyled>
                                )}
                                {mutationLoading && <Spinner size={SpinnerSize.SMALL} />}
                                {(canAssignFieldWeldEngineer || canReassignWeldForeman) &&
                                  personaType !== PersonaType.FOREMAN &&
                                  indexOf(assignedCrewMemberIds, user.id) != -1 && (
                                    <MenuItemStyled
                                      data-testid="weld-unassign-myself"
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        const unAssigncrewMemberInput: AssignCommodityInput = {
                                          commodityId: weld.id,
                                          crewMemberId: null,
                                          personaType:
                                            personaType === PersonaType.FIELD_WELD_ENGINEER
                                              ? personaType
                                              : PersonaType.FOREMAN,
                                        };
                                        const input: AssignCommoditiesInput = {
                                          input: [unAssigncrewMemberInput],
                                        };
                                        assignUnassignCommodityCrewMembers({
                                          variables: { input },
                                        });
                                      }}
                                    >
                                      {personaType === PersonaType.FIELD_WELD_ENGINEER
                                        ? t`Unassign Myself`
                                        : t`Unassign Foreman`}
                                    </MenuItemStyled>
                                  )}
                                {canReAssignForeman && !isVisibleOnWeldSubHeader && (
                                  <MenuItemStyled
                                    data-testid="weld-reassign"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      open();
                                    }}
                                  >
                                    <Trans>Reassign</Trans>
                                  </MenuItemStyled>
                                )}
                              </>
                            )}
                            {canRejectFitup &&
                              (weldStatus === CommodityStatusCode.READY_TO_WELD ||
                                weldStatus === CommodityStatusCode.VT_SENT_BACK ||
                                weldStatus === CommodityStatusCode.CLAIMED) && (
                                <MenuItemStyled
                                  data-testid="weld-send-back-piping"
                                  onMouseDown={(event) => {
                                    openSendBackPipingSidesheet();
                                    event.preventDefault();
                                  }}
                                >
                                  <Trans>Send Back to Piping</Trans>
                                </MenuItemStyled>
                              )}
                            {canViewWorkPackageModule && (
                              <MenuItemStyled
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  navigate(
                                    {
                                      pathname: `/work-packages/${id}`,
                                      search: `?tag=${tag}`,
                                    },
                                    {
                                      state: {
                                        drawerValues: {
                                          ...defaultValues,
                                          filterBy: WorkPackageFilterByTerm.ALL,
                                          searchScope: false,
                                        },
                                      },
                                    }
                                  );
                                }}
                              >
                                <Trans>Go To Work Package</Trans>
                              </MenuItemStyled>
                            )}
                          </Box>
                        </Box>
                      </MenuOptions>
                    )}
                  </PopperContainerStyled>
                </div>
              )}
            </Menu>
          </Box>
        )}
      </Box>
      {visibleCreateBlock && (
        <CreateBlock
          workPackageId={id}
          workPackageTag={tag}
          facilities={facilities ? facilities : []}
          assignedCrewMembers={workPackageOwners}
          weldCommodityAssignees={assignedCrewMembers}
          close={closeCreateBlockSideSheet}
          // @ts-ignore commodity conversion to commodity array
          refetchQueries={
            user?.crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER
              ? refetchQueries
              : [
                  'commodities',
                  'commodityStats',
                  'commodityStatsPriority',
                  'commodityWeeklyStats',
                  'commodityDashboardTabStats',
                ]
          }
          commodities={[weld]}
          multiBlock={false}
        />
      )}
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleWeldSidesheet && <WeldDetailsSidesheet weldId={weld.id} onClose={closeWeldSidesheet} />}
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleSendBackPipingSidesheet && (
            <WeldSendBackSideSheet
              close={closeSendBackPipingSidesheet}
              weld={weld}
              fitupProjectMilestoneDetails={fitupProjectMilestoneDetails}
              isSendBackToForeman={false}
              currentMilestone={weldCurrentMilestone}
              fitUpMilestone={fitUpMilestone}
              refetchQueries={
                weld?.status?.status === CommodityStatusCode.FITUP_INSPECTION ||
                weld?.status?.status === CommodityStatusCode.CLAIMED ||
                weld?.status?.status === CommodityStatusCode.READY_TO_WELD ||
                weld?.status?.status === CommodityStatusCode.VT_SENT_BACK
                  ? refetchQueries
                  : [
                      'commodities',
                      'commodityStats',
                      'commodityStatsPriority',
                      'commodityWeeklyStats',
                      'commodityDashboardTabStats',
                    ]
              }
            />
          )}
        </Suspense>
      </ErrorBoundary>

      {assignUnassignPriorityError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <AssignUnassignPriorityErrorModal updatePriorityError={assignUnassignPriorityError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
      {visible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <AssignWeldOwner onClose={close} weld={weld}></AssignWeldOwner>
          </Suspense>
        </ErrorBoundary>
      )}

      {visibleEditWeldDetailsSideSheet && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <EditWeldDetails weld={weld} closeSidesheet={closeEditWeldDetailsSideSheet} />
          </Suspense>
        </ErrorBoundary>
      )}

      {(unAssignCommodityCrewMembersErrors instanceof ApolloError ||
        (unAssignCommodityCrewMembersErrors instanceof Array && unAssignCommodityCrewMembersErrors?.length > 0)) && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <AssignCommodityCrewMembersErrorModal
              commodityAssignmentErrors={unAssignCommodityCrewMembersErrors}
              close={closedUnassignModal}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
